<script setup lang="ts">
import type {ConversationMessage} from "@/conversation/interfaces";
import {computed} from "vue";
import ConversationMessageStatus from "@/conversation/components/ConversationMessageStatus.vue";
import {Reply} from "@lucide/vue";

const props = defineProps<{
    message: ConversationMessage;
}>();

const isOutbound = computed(() => props.message.direction === 'outbound');
const isRCS = computed(() => props.message.channel === 'rcs');

const formattedTime = computed(() =>
    new Date(props.message.created_at).toLocaleString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
    })
);

const bubbleClasses = computed(() => {
    if (isOutbound.value && !isRCS.value) {
        return 'bg-blue-500 text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5';
    }
    if (isOutbound.value && isRCS.value) {
        return '';
    }
    return 'bg-gray-100 text-gray-900 rounded-2xl rounded-tl-sm px-3.5 py-2.5';
});
</script>

<template>
  <div class="flex w-full mb-1" :class="isOutbound ? 'justify-end' : 'justify-start'">
    <div
      class="flex flex-col"
      :class="[
        isRCS && isOutbound ? 'max-w-xs sm:max-w-sm' : 'max-w-[72%]',
        isOutbound ? 'items-end' : 'items-start'
      ]"
    >
      <div :class="bubbleClasses">
        <slot />
      </div>

      <div class="flex items-center gap-1.5 mt-1 px-1" :class="isOutbound ? 'flex-row-reverse' : 'flex-row'">
        <time class="text-[11px] text-gray-400">{{ formattedTime }}</time>
        <span class="text-[10px] text-gray-300 font-medium">{{ message.channel.toUpperCase() }}</span>
        <ConversationMessageStatus
          v-if="isOutbound"
          :status="message.status"
          :sent-at="message.sent_at"
          :delivered-at="message.delivered_at"
          :read-at="message.read_at"
          :failed-at="message.failed_at"
        />
        <div
          v-if="message.replies_count"
          class="flex items-center gap-0.5 text-[11px] text-gray-400"
        >
          <Reply class="w-3 h-3" />
          <span>{{ message.replies_count }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
